import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EyeIcon, EyeOffIcon, AlertCircleIcon, KeyIcon, UserIcon, LockIcon, MailIcon, ShieldIcon } from 'lucide-react';
const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(usernameOrEmail, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Invalid username/email or password');
      }
    } catch (err) {
      setError('An error occurred during login');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-gray-900" data-id="element-1356">
      {/* Left side - Branding/Illustration */}
      <div className="hidden md:flex md:w-1/2 bg-blue-600 text-white flex-col justify-center items-center p-12" data-id="element-1357">
        <div className="max-w-md mx-auto" data-id="element-1358">
          <div className="text-center" data-id="element-1359">
            <ShieldIcon className="h-16 w-16 mx-auto mb-6" data-id="element-1360" />
            <h1 className="text-3xl font-bold mb-2" data-id="element-1361">MMS Admin Portal</h1>
            <p className="text-blue-100 mb-8" data-id="element-1362">
              Secure Merchant Management System for financial institutions
            </p>
          </div>
          <div className="bg-blue-700/30 rounded-lg p-6 backdrop-blur-sm" data-id="element-1363">
            <h2 className="text-xl font-semibold mb-4" data-id="element-1364">System Features</h2>
            <ul className="space-y-2" data-id="element-1365">
              <li className="flex items-start" data-id="element-1366">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1367">
                  <span className="text-sm font-bold" data-id="element-1368">1</span>
                </div>
                <span data-id="element-1369">Complete merchant onboarding & KYC</span>
              </li>
              <li className="flex items-start" data-id="element-1370">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1371">
                  <span className="text-sm font-bold" data-id="element-1372">2</span>
                </div>
                <span data-id="element-1373">EMVCo compliant QR code generation</span>
              </li>
              <li className="flex items-start" data-id="element-1374">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1375">
                  <span className="text-sm font-bold" data-id="element-1376">3</span>
                </div>
                <span data-id="element-1377">Transaction monitoring & dispute management</span>
              </li>
              <li className="flex items-start" data-id="element-1378">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1379">
                  <span className="text-sm font-bold" data-id="element-1380">4</span>
                </div>
                <span data-id="element-1381">Advanced reporting & analytics</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12 md:w-1/2" data-id="element-1382">
        <div className="w-full max-w-md" data-id="element-1383">
          <div className="text-center mb-10" data-id="element-1384">
            <div className="md:hidden" data-id="element-1385">
              <ShieldIcon className="h-12 w-12 mx-auto mb-4 text-blue-600 dark:text-blue-400" data-id="element-1386" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1387">
                MMS Admin Portal
              </h1>
              <p className="text-gray-600 dark:text-gray-400" data-id="element-1388">
                Sign in to your account
              </p>
            </div>
            <div className="hidden md:block" data-id="element-1389">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1390">
                Welcome Back
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400" data-id="element-1391">
                Please sign in to your account
              </p>
            </div>
          </div>
          {error && <div className="mb-6 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-3 rounded-md flex items-center" data-id="element-1392">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-1393" />
              <span data-id="element-1394">{error}</span>
            </div>}
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1395">
            <div data-id="element-1396">
              <label htmlFor="username-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1397">
                Username or Email
              </label>
              <div className="relative" data-id="element-1398">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1399">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-1400" />
                </div>
                <input id="username-email" name="username-email" type="text" autoComplete="username" required className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Enter your username or email" value={usernameOrEmail} onChange={e => setUsernameOrEmail(e.target.value)} data-id="element-1401" />
              </div>
            </div>
            <div data-id="element-1402">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1403">
                Password
              </label>
              <div className="relative" data-id="element-1404">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1405">
                  <LockIcon className="h-5 w-5 text-gray-400" data-id="element-1406" />
                </div>
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 pr-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} data-id="element-1407" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-1408">
                  {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-1409" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-1410" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-1411">
              <div className="flex items-center" data-id="element-1412">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1413" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-1414">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-1415">
                <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300" data-id="element-1416">
                  Forgot your password?
                </Link>
              </div>
            </div>
            <div data-id="element-1417">
              <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transition-colors duration-200" data-id="element-1418">
                {isLoading ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1419">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1420"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1421"></path>
                    </svg>
                    Signing in...
                  </> : 'Sign in'}
              </button>
            </div>
          </form>
          <div className="mt-8 text-center" data-id="element-1422">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1423">
              Demo credentials:{' '}
              <span className="font-medium" data-id="element-1424">adminmaker / Test@1234</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Login;